import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CheckCheck,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Inbox,
  Layers,
  Mail,
  MessageCircle,
  MinusCircle,
  Smartphone,
  X,
  XCircle,
} from "lucide-react";

import {
  NotificacaoCanal,
  NotificacaoCanalMap,
  NotificacaoTemplateMap,
  StatusEnvioNotificacao,
  StatusEnvioNotificacaoMap,
} from "@workflow/types";
import {
  NotificacaoLogDTO,
  NotificacaoLogFiltrosCriteria,
  NotificacaoLogFiltrosOptions,
} from "@/types/notificacao-log.type";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import {
  notificacoesAdminQueries,
  notificacoesAdminKeys,
} from "@/services/api/queries/notificacoes-admin.queries";

import { Person } from "@/components/shared/person";
import { Button } from "@/components/ui/button";
import { formatPhoneNumber, getPhoneNumberDigits } from "@/utils/phone-format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const CANAL_CONFIG: Record<NotificacaoCanal, { icon: typeof Mail; className: string }> = {
  [NotificacaoCanal.EMAIL]: {
    icon: Mail,
    className: "border-blue-500/20 bg-blue-500/[0.07] text-blue-700 dark:text-blue-300",
  },
  [NotificacaoCanal.WHATSAPP]: {
    icon: MessageCircle,
    className: "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
  },
  [NotificacaoCanal.SMS]: {
    icon: Smartphone,
    className: "border-violet-500/20 bg-violet-500/[0.07] text-violet-700 dark:text-violet-300",
  },
  [NotificacaoCanal.INBOX]: {
    icon: Inbox,
    className: "border-amber-500/20 bg-amber-500/[0.07] text-amber-700 dark:text-amber-300",
  },
};

/** Canais cujo destino é telefone — mudam a máscara e a formatação da coluna Destino. */
const ehTelefone = (canal: NotificacaoCanal) =>
  canal === NotificacaoCanal.WHATSAPP || canal === NotificacaoCanal.SMS;

const STATUS_CONFIG: Record<StatusEnvioNotificacao, { icon: typeof Mail; className: string }> = {
  [StatusEnvioNotificacao.ENVIADA]: {
    icon: CheckCircle2,
    className: "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
  },
  [StatusEnvioNotificacao.IGNORADA]: {
    icon: MinusCircle,
    className: "border-amber-500/20 bg-amber-500/[0.07] text-amber-700 dark:text-amber-300",
  },
  [StatusEnvioNotificacao.FALHA]: {
    icon: XCircle,
    className: "border-red-500/20 bg-red-500/[0.07] text-red-700 dark:text-red-300",
  },
};

/** Só o Inbox reporta leitura (webhook próprio) — os demais canais não têm como saber. */
const CANAIS_COM_RASTREIO_LEITURA = new Set<NotificacaoCanal>([NotificacaoCanal.INBOX]);

const LEITURA_CONFIG: Record<"lida" | "naoLida", { icon: typeof Mail; className: string }> = {
  // Sólido de propósito: é o único badge "positivo" da coluna, precisa se destacar
  // do resto da linha (canal/status já competem por atenção com o mesmo estilo translúcido).
  lida: {
    icon: CheckCheck,
    className: "border-transparent bg-blue-600 text-white dark:bg-blue-500",
  },
  naoLida: {
    icon: Clock,
    className: "border-border/60 bg-muted text-muted-foreground",
  },
};

/** Máscara do destino: mostra só o suficiente para reconhecer, sem expor o dado por completo. */
const maskDestino = (destino: string, canal: NotificacaoCanal): string => {
  if (ehTelefone(canal)) {
    const digits = getPhoneNumberDigits(destino);
    return `•••• ${digits.slice(-4)}`;
  }

  // Inbox: o destino é o id da identidade, opaco — só o fim serve para conferir qual é.
  if (canal === NotificacaoCanal.INBOX) return `•••• ${destino.slice(-4)}`;

  const [local, dominio] = destino.split("@");
  if (!dominio) return "••••";
  return `${local.slice(0, 1)}${"•".repeat(Math.max(local.length - 1, 3))}@${dominio}`;
};

const Badge = ({
  config,
  label,
}: {
  config?: { icon: typeof Mail; className: string };
  label: string;
}) => {
  const Icon = config?.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border py-1 pl-1.5 pr-2 text-[11px] font-medium",
        config?.className ?? "border-border/60 bg-muted text-muted-foreground",
      )}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0 opacity-70" />}
      {label}
    </span>
  );
};

/**
 * Lida (Inbox, com data — relativo com a data completa no hover), não lida (Inbox,
 * sem data) ou traço neutro pros demais canais, que não têm como reportar leitura
 * (repetir "Sem rastreio" em toda linha de e-mail/WhatsApp/SMS é só ruído).
 */
const LeituraBadge = ({ log }: { log: NotificacaoLogDTO }) => {
  if (log.dataLeitura) {
    const data = new Date(log.dataLeitura);
    return (
      <span title={format(data, "dd/MM/yyyy HH:mm:ss")}>
        <Badge
          config={LEITURA_CONFIG.lida}
          label={formatDistanceToNow(data, { addSuffix: true, locale: ptBR })}
        />
      </span>
    );
  }
  if (CANAIS_COM_RASTREIO_LEITURA.has(log.canal)) {
    return <Badge config={LEITURA_CONFIG.naoLida} label="Não lida" />;
  }
  return <span className="text-xs text-muted-foreground/60">—</span>;
};

/** Diálogo com o conteúdo efetivamente enviado. E-mail renderiza HTML isolado num iframe; WhatsApp vira um balão de chat. */
const ConteudoDialog = ({
  log,
  onClose,
}: {
  log: NotificacaoLogDTO | null;
  onClose: () => void;
}) => {
  const isEmail = log?.canal === NotificacaoCanal.EMAIL;
  const titulo = log
    ? log.assunto || NotificacaoTemplateMap[log.template] || log.template
    : "Conteúdo da notificação";

  return (
    <Dialog open={!!log} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2">
              {log && (
                <Badge
                  config={CANAL_CONFIG[log.canal]}
                  label={NotificacaoCanalMap[log.canal] || log.canal}
                />
              )}
              {log && (
                <Badge
                  config={STATUS_CONFIG[log.status]}
                  label={StatusEnvioNotificacaoMap[log.status] || log.status}
                />
              )}
              {log?.criadoEm && (
                <span className="text-xs text-muted-foreground">
                  {format(new Date(log.criadoEm), "dd/MM/yyyy HH:mm")}
                </span>
              )}
              {log && <LeituraBadge log={log} />}
            </div>
          </DialogDescription>
        </DialogHeader>

        {log?.erro && (
          <p className="rounded-md border border-red-500/20 bg-red-500/[0.07] px-3 py-2 text-xs text-red-700 dark:text-red-300">
            {log.erro}
          </p>
        )}

        {log?.conteudo ? (
          isEmail ? (
            <iframe
              title="Conteúdo do e-mail"
              srcDoc={log.conteudo}
              className="h-[420px] w-full rounded-md border bg-white"
              sandbox=""
            />
          ) : (
            <div className="max-h-[420px] overflow-auto rounded-md border bg-muted/30 p-4">
              <div className="relative max-w-[85%] whitespace-pre-wrap rounded-lg rounded-tl-none bg-emerald-100 px-3 py-2 text-sm text-emerald-950 shadow-sm dark:bg-emerald-900/40 dark:text-emerald-50">
                {log.conteudo}
              </div>
            </div>
          )
        ) : (
          <p className="text-sm italic text-muted-foreground">Sem conteúdo registrado.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export function NotificacaoLogListTable() {
  const [conteudoLog, setConteudoLog] = useState<NotificacaoLogDTO | null>(null);
  const [destinosRevelados, setDestinosRevelados] = useState<Set<number>>(new Set());

  const toggleDestinoRevelado = (id: number) =>
    setDestinosRevelados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { tableProps, isError, invalidate, setFilter, criteria } = useServerDataTable<
    NotificacaoLogDTO,
    NotificacaoLogFiltrosOptions,
    NotificacaoLogFiltrosCriteria
  >({
    queryOptions: notificacoesAdminQueries.logsList,
    filters: notificacoesAdminQueries.logsFiltros,
    invalidateKey: notificacoesAdminKeys.logsList,
  });

  const disparoAtivo = criteria?.disparoId;

  const columns = useMemo<Column<NotificacaoLogDTO>[]>(
    () => [
      {
        key: "destinatario",
        title: "Destinatário",
        width: "min-w-[220px]",
        render: (log) =>
          log.destinatario ? (
            <Person.Root pessoa={log.destinatario} asClickable className="flex items-center gap-2">
              <Person.Avatar />
              <Person.Name className="text-sm" />
            </Person.Root>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">—</span>
          ),
        raw: (log) => log.destinatario?.nome || "",
      },
      {
        key: "destino",
        title: "Destino",
        width: "min-w-[200px]",
        render: (log) => {
          if (!log.destino)
            return <span className="font-mono text-xs text-muted-foreground">—</span>;

          const revelado = destinosRevelados.has(log.id);
          const texto = revelado
            ? ehTelefone(log.canal)
              ? formatPhoneNumber(log.destino)
              : log.destino
            : maskDestino(log.destino, log.canal);

          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-xs text-muted-foreground">{texto}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                title={revelado ? "Ocultar destino" : "Revelar destino"}
                onClick={() => toggleDestinoRevelado(log.id)}
              >
                {revelado ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </span>
          );
        },
        raw: (log) => log.destino || "",
      },
      {
        key: "notificacaoLog.canal",
        title: "Canal",
        sortable: true,
        width: "min-w-[130px]",
        render: (log) => (
          <Badge
            config={CANAL_CONFIG[log.canal]}
            label={NotificacaoCanalMap[log.canal] || log.canal}
          />
        ),
        raw: (log) => NotificacaoCanalMap[log.canal] || log.canal,
      },
      {
        key: "notificacaoLog.template",
        title: "Notificação",
        sortable: true,
        width: "min-w-[220px]",
        render: (log) => (
          <span className="text-xs text-muted-foreground">
            {NotificacaoTemplateMap[log.template] || log.template}
          </span>
        ),
        raw: (log) => NotificacaoTemplateMap[log.template] || log.template || "",
      },
      {
        key: "notificacaoLog.status",
        title: "Status",
        sortable: true,
        width: "min-w-[120px]",
        render: (log) => (
          <Badge
            config={STATUS_CONFIG[log.status]}
            label={StatusEnvioNotificacaoMap[log.status] || log.status}
          />
        ),
        raw: (log) => StatusEnvioNotificacaoMap[log.status] || log.status,
      },
      {
        key: "notificacaoLog.dataLeitura",
        title: "Lida",
        sortable: true,
        width: "min-w-[140px]",
        render: (log) => <LeituraBadge log={log} />,
        raw: (log) => (log.dataLeitura ? new Date(log.dataLeitura).toISOString() : ""),
      },
      {
        key: "notificacaoLog.criadoEm",
        title: "Data",
        sortable: true,
        width: "min-w-[160px]",
        render: (log) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {log.criadoEm ? format(new Date(log.criadoEm), "dd/MM/yyyy HH:mm") : "—"}
          </span>
        ),
        raw: (log) => (log.criadoEm ? new Date(log.criadoEm).toISOString() : ""),
      },
      {
        key: "conteudo",
        title: "Conteúdo",
        width: "min-w-[100px]",
        render: (log) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => setConteudoLog(log)}
          >
            <Eye className="h-3.5 w-3.5" />
            Ver
          </Button>
        ),
        raw: (log) => log.conteudo || "",
      },
      {
        key: "disparo",
        title: "Disparo",
        width: "min-w-[120px]",
        render: (log) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            title="Ver todas as notificações deste mesmo disparo"
            onClick={() => setFilter({ disparoId: log.disparoId })}
          >
            <Layers className="h-3.5 w-3.5" />
            Ver grupo
          </Button>
        ),
        raw: (log) => log.disparoId || "",
      },
    ],
    [setFilter, destinosRevelados],
  );

  return (
    <>
      {disparoAtivo && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-primary/20 bg-primary/[0.06] px-3 py-2 text-sm">
          <span className="flex items-center gap-2 text-muted-foreground">
            <Layers className="h-4 w-4 text-primary" />
            Mostrando as notificações de um único disparo.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => setFilter({ disparoId: undefined })}
          >
            <X className="h-3.5 w-3.5" />
            Limpar
          </Button>
        </div>
      )}

      <DataTable
        {...tableProps}
        storageKey="notificacao-logs-admin-v1"
        columns={columns}
        defaultSortColumn="notificacaoLog.criadoEm"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar por destinatário ou destino..."
        emptyMessage={
          isError
            ? "Não foi possível carregar o histórico. Tente recarregar."
            : "Nenhum registro encontrado."
        }
        isError={isError}
        onRetry={invalidate}
        exportFilename="historico-notificacoes"
      />

      <ConteudoDialog log={conteudoLog} onClose={() => setConteudoLog(null)} />
    </>
  );
}
